// src/admin/pages/tabs/TeamsListTab.js
import React, { useState, useEffect } from 'react';
import { UserCheck, Database, Users, Calendar } from 'lucide-react';
import { supabase } from '../../../config';
import partyTeamService from '../../../products/dataroom/services/partyTeamService';

const TeamsListTab = () => {
  const [teams, setTeams] = useState([]);
  const [parties, setParties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch all teams
      const { data: teamsData, error: teamsError } = await partyTeamService.getAllTeams();
      if (teamsError) throw teamsError;

      // Fetch all parties for mapping
      const { data: partiesData, error: partiesError } = await partyTeamService.getAllParties();
      if (partiesError) throw partiesError;

      // Fetch all projects for mapping
      const { data: projectsData, error: projectsError } = await supabase
        .from('dataroom_projects')
        .select('id, project_name')
        .order('project_name');

      if (projectsError) throw projectsError;

      setTeams(Array.isArray(teamsData?.data) ? teamsData.data : (Array.isArray(teamsData) ? teamsData : []));
      setParties(Array.isArray(partiesData?.data) ? partiesData.data : (Array.isArray(partiesData) ? partiesData : []));
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      setTeams([]);
      setParties([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? party.name : `Party #${partyId}`;
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.project_name : `Project #${projectId}`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading teams...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Teams</h2>
          <p className="text-gray-600 mt-1">View all teams across all dealrooms</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <UserCheck className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Teams</p>
              <p className="text-2xl font-bold text-gray-900">{teams.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unique Parties</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set((Array.isArray(teams) ? teams : []).map(t => t.party_id)).size}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Database className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unique Dealrooms</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set((Array.isArray(teams) ? teams : []).map(t => t.project_id)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dealroom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(teams) && teams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <UserCheck className="h-5 w-5 text-purple-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {team.name || 'Unnamed Team'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {team.description || 'No description'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-gray-400 mr-1" />
                      {getPartyName(team.party_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Database className="h-4 w-4 text-gray-400 mr-1" />
                      {getProjectName(team.project_id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {team.team_type || 'standard'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {team.created_at 
                        ? new Date(team.created_at).toLocaleDateString()
                        : 'N/A'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {teams.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
            <p className="mt-1 text-sm text-gray-500">No teams have been created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsListTab;

